import {Button, ColorPicker, Input, Select, Switch} from "antd";
import type {ColorValueType} from "antd/es/color-picker/interface";
import {useEffect, useMemo, useState} from "react";
import useUserContext from "../../../hooks/useUserContext";
import useAppContext from "../../../hooks/useAppContext";
import {getWidgetSettings} from "../../../api/getWidget";
import CopyToClipboardCode from "../../../components/copyToClipboardCode/CopyToClipboardCode";
import {generateWidgetApiKey} from "../../../api/generateWidgetApiKey";
import {updateWidget, WIDGET_TYPES} from "../../../api/updateWidget";
import Loader from "../../../components/loader/Loader";
import useToast from "../../../hooks/useToast";
import {ToastSkin} from "../../../context/ToastContext";
import {getWidgetLocaleConfig, WIDGET_LANGUAGE_CONFIG, WidgetLanguage} from "./widgetLocales";
import ImagePicker from "../../../components/forms/imagePicker/ImagePicker";
import {getWidgetAvatarUploadUrl} from "../../../api/getWidgetAvatarUploadUrl";
import axios from "axios";
import {BASE_URL} from "../../../contants/network";
import {CheckCircleFilled, DownOutlined, LockFilled, UndoOutlined} from "@ant-design/icons";
import useGlobalModal from "../../../hooks/useGlobalModal";
import KnowledgeBaseEditor from "../../knowledgeBase/KnowledgeBaseEditor";
import Page from "../../../components/page/Page";
import { ListKnowledgeBaseDocuments } from "../../../api/v2/knowledgeBase";
import Panel from "../../../components/panel/Panel";
import botHeyImage from "../../../assets/images/bot-hey.png";

const DEFAULT_COLOR: NonNullable<ColorValueType> = [
    {
        color: '#6389ff',
        percent: 0,
    },
    {
        color: '#311cbd',
        percent: 100,
    },
];

const parseGradientColorValue = (value?: string | null): ColorValueType => {
    if (!value || !value.includes('gradient')) {
        return value || null;
    }

    const explicitMatches = Array.from(value.matchAll(/(#[0-9a-fA-F]{3,8}|rgba?\([^)]+\))\s+(\d+)%/g));
    if (explicitMatches.length) {
        return explicitMatches.map((match) => ({
            color: match[1],
            percent: Number(match[2]),
        }));
    }

    const simpleMatches = Array.from(value.matchAll(/(#[0-9a-fA-F]{3,8}|rgba?\([^)]+\))/g)).map((match) => match[1]);
    if (!simpleMatches.length) {
        return DEFAULT_COLOR;
    }

    if (simpleMatches.length === 1) {
        return simpleMatches[0];
    }

    if (simpleMatches.length === 2) {
        return [
            { color: simpleMatches[0], percent: 0 },
            { color: simpleMatches[1], percent: 100 },
        ];
    }

    const lastIndex = simpleMatches.length - 1;
    return simpleMatches.map((color, index) => ({
        color,
        percent: Math.round((index / lastIndex) * 100),
    }));
};

export const SupportWidgetPanel = () => {
    const mergeLocaleTranslations = (language: WidgetLanguage, overrides?: Record<string, string>) => {
        const localeConfig = getWidgetLocaleConfig(language);
        return {
            ...localeConfig.translations,
            ...(overrides || {}),
        };
    };

    const [apiKey, setApiKey] = useState('');
    const defaults = {
        colors: 'linear-gradient(45deg, #6389ff, #311cbd)',
        textColor1: '#311cbd',
        textColor2: '#44b86e',
        direction: 'ltr',
        QA: [{ q: '', a: '' }, { q: '', a: '' }, { q: '', a: '' }],
    }
    const [colors, setColors] = useState(defaults.colors);
    const [gradientPickerValue, setGradientPickerValue] = useState<ColorValueType>(DEFAULT_COLOR);
    const [textColor1, setTextColor1] = useState(defaults.textColor1);
    const [textColor2, setTextColor2] = useState(defaults.textColor2);
    const [direction, setDirection] = useState(defaults.direction);
    const [translations, setTranslations] = useState<any>({});
    const [widgetLanguage, setWidgetLanguage] = useState<WidgetLanguage>('en');
    const [useCustomTranslations, setUseCustomTranslations] = useState(false);
    const { data: { shop }} = useAppContext();
    const { selectedDomain } = useUserContext();
    const { setIsPricingPlanModalOpen } = useGlobalModal();
    const [isLoading, setIsLoading] = useState(false);
    const [previewVersion, setPreviewVersion] = useState(0);
    const [isLoaded, setIsLoaded] = useState(false);
    const { showToast, closeToast } = useToast();
    const [QA, setQA] = useState<{ q: string, a: string }[]>(defaults.QA);
    const [isActive, setIsActive] = useState(false);
    const [widgetType, setWidgetType] = useState<WIDGET_TYPES>(WIDGET_TYPES.SUPPORT);
    const [isEmbedSectionOpen, setIsEmbedSectionOpen] = useState(false);
    const [humanAgentName, setHumanAgentName] = useState('');
    const [humanAgentImageUrl, setHumanAgentImageUrl] = useState('');
    const [humanAgentImageFileKey, setHumanAgentImageFileKey] = useState('');
    const [isUploadingHumanAgentImage, setIsUploadingHumanAgentImage] = useState(false);
    const isShopContextReady = !!shop.isLoaded && !!shop.data?.id;
    const isAiEligible = !!shop.data?.pricingPlan?.withAI;
    const resolvedWidgetType = isAiEligible ? widgetType : WIDGET_TYPES.SUPPORT;
    const [initialSettingsSnapshot, setInitialSettingsSnapshot] = useState<string>('');
    const [knowledgeBaseItemsCount, setKnowledgeBaseItemsCount] = useState(0);
    const [isKnowledgeBasePanelOpen, setIsKnowledgeBasePanelOpen] = useState(false);
    const [knowledgeBaseCreateToken, setKnowledgeBaseCreateToken] = useState(0);

    const buildSettingsSnapshot = ({
        colors,
        textColor1,
        textColor2,
        widgetLanguage,
        direction,
        translations,
        QA,
        useCustomTranslations,
        humanAgentName,
        humanAgentImageUrl,
        humanAgentImageFileKey,
        isActive,
        widgetType,
    }: {
        colors: string;
        textColor1: string;
        textColor2: string;
        widgetLanguage: WidgetLanguage;
        direction: string;
        translations: Record<string, string>;
        QA: { q: string; a: string }[];
        useCustomTranslations: boolean;
        humanAgentName: string;
        humanAgentImageUrl: string;
        humanAgentImageFileKey: string;
        isActive: boolean;
        widgetType: WIDGET_TYPES;
    }) => JSON.stringify({
        colors,
        textColor1,
        textColor2,
        widgetLanguage,
        direction,
        translations,
        QA,
        useCustomTranslations,
        humanAgentName,
        humanAgentImageUrl,
        humanAgentImageFileKey,
        isActive,
        widgetType,
    });

    const uploadWidgetAgentAvatar = async (base64Image: string | null) => {
        if (!base64Image || !selectedDomain) return;
        setIsUploadingHumanAgentImage(true);
        try {
            const uploadResponse = await getWidgetAvatarUploadUrl(selectedDomain);
            const uploadUrl = uploadResponse?.data?.url;
            const fileKey = uploadResponse?.data?.fileKey;
            if (!uploadUrl || !fileKey || !shop.data?.id) {
                showToast({ skin: ToastSkin.error, text: "Couldn't prepare the avatar upload." });
                return;
            }
            const blob = await fetch(base64Image).then((response) => response.blob());
            await axios.put(uploadUrl, blob, {
                headers: {
                    "Content-Type": "image/png",
                },
            });
            setHumanAgentImageFileKey(fileKey);
            setHumanAgentImageUrl(`${BASE_URL}widget/avatar?shopId=${shop.data.id}&fileKey=${encodeURIComponent(fileKey)}`);
        } catch (error: any) {
            showToast({ skin: ToastSkin.error, text: "Couldn't upload the avatar image." });
        } finally {
            setIsUploadingHumanAgentImage(false);
        }
    };

    const loadKnowledgeBaseSummary = async () => {
        if (!selectedDomain) return 0;
        const response = await ListKnowledgeBaseDocuments(selectedDomain);
        if (!response.ok || !response.data?.documents) return 0;
        return response.data.documents.length;
    };

    const getWidget = async () => {
        if(selectedDomain) {
            const response = await getWidgetSettings(selectedDomain, resolvedWidgetType);
            let nextIsActive = false;
            let nextWidgetType = resolvedWidgetType;
            let nextColors = defaults.colors;
            let nextTextColor1 = defaults.textColor1;
            let nextTextColor2 = defaults.textColor2;
            let nextWidgetLanguage: WidgetLanguage = 'en';
            let nextUseCustomTranslations = false;
            let nextDirection = defaults.direction;
            let nextTranslations = getWidgetLocaleConfig('en').translations;
            let nextQA = defaults.QA;
            let nextHumanAgentName = '';
            let nextHumanAgentImageUrl = '';
            let nextHumanAgentImageFileKey = '';

            if(response?.data?.apiKey) {
                setApiKey(response.data.apiKey);
                nextIsActive = response.data.active;
                setIsActive(nextIsActive);
                if(response.data.widgetType) {
                    nextWidgetType = response.data.widgetType;
                    setWidgetType(nextWidgetType);
                }
            } else {
                setApiKey('');
                setIsActive(false);
            }
            if(response?.data?.meta) {
                try {
                    const meta = JSON.parse(response.data.meta);
                    nextColors = meta.colors || defaults.colors;
                    nextTextColor1 = meta.textColor1 || defaults.textColor1;
                    nextTextColor2 = meta.textColor2 || defaults.textColor2;
                    const nextLanguage = meta.widgetLanguage || 'en';
                    const localeConfig = getWidgetLocaleConfig(nextLanguage);
                    nextWidgetLanguage = nextLanguage;
                    nextUseCustomTranslations = !!meta.useCustomTranslations;
                    nextDirection = meta.direction || localeConfig.direction;
                    nextTranslations = mergeLocaleTranslations(nextLanguage, meta.translations);
                    nextQA = meta.QA || defaults.QA;
                    nextHumanAgentName = meta.humanAgentName || '';
                    nextHumanAgentImageFileKey = meta.humanAgentImageFileKey || '';
                    nextHumanAgentImageUrl = meta.humanAgentImageFileKey && shop.data?.id
                        ? `${BASE_URL}widget/avatar?shopId=${shop.data.id}&fileKey=${encodeURIComponent(meta.humanAgentImageFileKey)}`
                        : (meta.humanAgentImageUrl || '')
                    ;

                    setColors(nextColors);
                    setGradientPickerValue(parseGradientColorValue(nextColors));
                    setTextColor1(nextTextColor1);
                    setTextColor2(nextTextColor2);
                    setWidgetLanguage(nextWidgetLanguage);
                    setUseCustomTranslations(nextUseCustomTranslations);
                    setDirection(nextDirection);
                    setTranslations(nextTranslations);
                    setQA(nextQA);
                    setHumanAgentName(nextHumanAgentName);
                    setHumanAgentImageFileKey(nextHumanAgentImageFileKey);
                    setHumanAgentImageUrl(nextHumanAgentImageUrl);
                } catch(error: any) {
                    console.log("error: ", error);
                }
            } else {
                setColors(nextColors);
                setGradientPickerValue(parseGradientColorValue(nextColors));
                setTextColor1(nextTextColor1);
                setTextColor2(nextTextColor2);
                setWidgetLanguage(nextWidgetLanguage);
                setUseCustomTranslations(nextUseCustomTranslations);
                setDirection(nextDirection);
                setTranslations(nextTranslations);
                setQA(nextQA);
                setHumanAgentName(nextHumanAgentName);
                setHumanAgentImageFileKey(nextHumanAgentImageFileKey);
                setHumanAgentImageUrl(nextHumanAgentImageUrl);
            }
            setInitialSettingsSnapshot(buildSettingsSnapshot({
                colors: nextColors,
                textColor1: nextTextColor1,
                textColor2: nextTextColor2,
                widgetLanguage: nextWidgetLanguage,
                direction: nextDirection,
                translations: nextUseCustomTranslations ? nextTranslations : getWidgetLocaleConfig(nextWidgetLanguage).translations,
                QA: nextQA,
                useCustomTranslations: nextUseCustomTranslations,
                humanAgentName: nextHumanAgentName,
                humanAgentImageUrl: nextHumanAgentImageUrl,
                humanAgentImageFileKey: nextHumanAgentImageFileKey,
                isActive: nextIsActive,
                widgetType: isAiEligible ? nextWidgetType : WIDGET_TYPES.SUPPORT,
            }));
        }
    }

    const loadPageData = async () => {
        if (!selectedDomain || !isShopContextReady) return;
        setIsLoaded(false);
        const [, knowledgeBaseCount] = await Promise.all([
            getWidget(),
            loadKnowledgeBaseSummary(),
        ]);
        setKnowledgeBaseItemsCount(knowledgeBaseCount || 0);
        setIsLoaded(true);
    };

    const updateApiKeyClick = async () => {
        showToast({ skin: ToastSkin.neutral, text: "Are you sure you want to rotate the API Key? That will break any use of the current API Key.", buttons: [
            { text: 'Yes', onClick: updateApiKey },
            { text: 'No, Cancel', onClick: closeToast }
        ] });
    }

    const updateApiKey = async () => {
        if(selectedDomain) {
            setIsLoading(true);
            const response = await generateWidgetApiKey(selectedDomain, resolvedWidgetType);
            if(response?.data?.apiKey) {
                setApiKey(response.data.apiKey);
            }
            setIsLoading(false);
        }
        closeToast();
    }

    const updateSupportWidget = async (isActive: boolean) => {
        if(selectedDomain) {
            setIsLoading(true);
            const resolvedTranslations = useCustomTranslations
                ? mergeLocaleTranslations(widgetLanguage, translations)
                : getWidgetLocaleConfig(widgetLanguage).translations;
            const widgetResponse = await updateWidget(selectedDomain, resolvedWidgetType, JSON.stringify({
                colors,
                textColor1,
                textColor2,
                widgetLanguage,
                direction,
                translations: resolvedTranslations,
                QA,
                botLanguage: widgetLanguage,
                useCustomTranslations,
                humanAgentName,
                humanAgentImageUrl,
                humanAgentImageFileKey,
            }), isActive);
            if (widgetResponse?.status && widgetResponse.status >= 400) {
                setIsLoading(false);
                showToast({ skin: ToastSkin.danger, text: "Failed to update widget settings." });
                return;
            }

            setInitialSettingsSnapshot(buildSettingsSnapshot({
                colors,
                textColor1,
                textColor2,
                widgetLanguage,
                direction,
                translations: resolvedTranslations,
                QA,
                useCustomTranslations,
                humanAgentName,
                humanAgentImageUrl,
                humanAgentImageFileKey,
                isActive,
                widgetType: resolvedWidgetType,
            }));
            setIsLoading(false);
        }
    }

    useEffect(() => {
        loadPageData();
    }, [selectedDomain, isShopContextReady]);

    useEffect(() => {
        setPreviewVersion(previewVersion + 1);
    }, [colors, direction]);
    useEffect(() => {
        const localeConfig = getWidgetLocaleConfig(widgetLanguage);
        setDirection(localeConfig.direction);
        if (!useCustomTranslations) {
            setTranslations(localeConfig.translations);
        } else {
            setTranslations((current: Record<string, string>) => mergeLocaleTranslations(widgetLanguage, current));
        }
    }, [widgetLanguage, useCustomTranslations]);

    useEffect(() => {
        if (!isAiEligible && widgetType !== WIDGET_TYPES.SUPPORT) {
            setWidgetType(WIDGET_TYPES.SUPPORT);
        }
    }, [isAiEligible, widgetType]);

    const currentSettingsSnapshot = useMemo(() => {
        const resolvedTranslations = useCustomTranslations
            ? mergeLocaleTranslations(widgetLanguage, translations)
            : getWidgetLocaleConfig(widgetLanguage).translations;
        return buildSettingsSnapshot({
            colors,
            textColor1,
            textColor2,
            widgetLanguage,
            direction,
            translations: resolvedTranslations,
            QA,
            useCustomTranslations,
            humanAgentName,
            humanAgentImageUrl,
            humanAgentImageFileKey,
            isActive,
            widgetType: resolvedWidgetType,
        });
    }, [
        colors,
        textColor1,
        textColor2,
        widgetLanguage,
        direction,
        translations,
        QA,
        useCustomTranslations,
        humanAgentName,
        humanAgentImageUrl,
        humanAgentImageFileKey,
        isActive,
        resolvedWidgetType,
    ]);

    const isSettingsDirty = !!initialSettingsSnapshot && currentSettingsSnapshot !== initialSettingsSnapshot;

    const closeKnowledgeBasePanel = () => {
        setIsKnowledgeBasePanelOpen(false);
        loadKnowledgeBaseSummary().then((count) => setKnowledgeBaseItemsCount(count || 0));
    };

    return (
        <Page
            className="widget-container widget-settings-page"
            title="Support Widget"
            backButton={true}
            titleRight={isShopContextReady && isLoaded ? (
                <div className="flex activation-toggle widget-page-header-toggle">
                    <Switch value={isActive} onChange={async () => {
                        setIsActive(!isActive);
                        await updateSupportWidget(!isActive);
                    }} />
                    <span>{isActive ? 'Enabled' : 'Disabled'}</span>
                </div>
            ) : null}
        >
        <div className="widget-panel">
            {isShopContextReady && isLoaded ?
                <div className="editor-container">
                    <div className="widget-settings-grid">
                    <div className="widget-settings-left">
                    <div className="widget-section widget-section-primary">
                        <div className="widget-section-block">
                            <div className="widget-section-header">
                                <div className="widget-section-title">Agent Identity</div>
                                <div className="widget-section-subtitle">This identity is shown when the conversation is presented as agent-led, including human handoff states.</div>
                            </div>
                            <div className="widget-agent-identity">
                                <div className="widget-agent-avatar-picker">
                                    <ImagePicker
                                        onImageConfirmed={uploadWidgetAgentAvatar}
                                        previewUrl={humanAgentImageUrl}
                                        isLoading={isUploadingHumanAgentImage}
                                        size={96}
                                    />
                                </div>
                                <div className="widget-agent-fields">
                                    <label>Human Agent Name</label>
                                    <Input
                                        value={humanAgentName}
                                        onChange={(e) => setHumanAgentName(e.target.value)}
                                        placeholder="Avi"
                                        maxLength={60}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="widget-section-divider widget-ai-primary-divider" />
                        <div className="widget-ai-bot-block">
                        <img
                            className={`widget-ai-bot-illustration ${!isAiEligible ? 'upsell' : ''}`}
                            src={botHeyImage}
                            alt=""
                        />
                        <div className="widget-section-header">
                            <div className="widget-ai-layer-header">
                                <div className={`widget-ai-layer-copy ${!isAiEligible ? 'upsell' : ''}`}>
                                    <div>
                                    <div className="widget-section-title widget-ai-section-title">
                                        {!isAiEligible ? <LockFilled className="widget-ai-layer-lock" /> : null}
                                        <span>AI Bot</span>
                                    </div>
                                    <div className="widget-section-subtitle">
                                        {isAiEligible
                                            ? 'Bot first. If needed, it moves the chat to a representative.'
                                            : 'Available only on pricing plans with AI access.'}
                                    </div>
                                    </div>
                                </div>
                                {isAiEligible ? (
                                    <div className="widget-inline-toggle">
                                        <div className="widget-inline-toggle-label">
                                            {resolvedWidgetType === WIDGET_TYPES.SUPPORT_AI ? 'On' : 'Off'}
                                        </div>
                                        <Switch
                                            value={resolvedWidgetType === WIDGET_TYPES.SUPPORT_AI}
                                            onChange={(value) => setWidgetType(value ? WIDGET_TYPES.SUPPORT_AI : WIDGET_TYPES.SUPPORT)}
                                        />
                                    </div>
                                ) : (
                                    <div className="widget-ai-layer-plan-badge">
                                        Starter Plus and above
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="widget-section-block">
                            {!isAiEligible ? (
                                <div className="widget-ai-layer-benefits">
                                    <div className="widget-ai-layer-benefit">
                                        <CheckCircleFilled className="widget-ai-layer-benefit-check" />
                                        <span>Auto replies instantly</span>
                                    </div>
                                    <div className="widget-ai-layer-benefit">
                                        <CheckCircleFilled className="widget-ai-layer-benefit-check" />
                                        <span>Increases conversations from chats</span>
                                    </div>
                                    <div className="widget-ai-layer-benefit">
                                        <CheckCircleFilled className="widget-ai-layer-benefit-check" />
                                        <span>Reduces manual support workload</span>
                                    </div>
                                    <Button
                                        className="widget-ai-layer-cta"
                                        type="default"
                                        size="large"
                                        onClick={() => setIsPricingPlanModalOpen(true)}
                                    >
                                        See plans with AI
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                        </div>

                        {resolvedWidgetType === WIDGET_TYPES.SUPPORT_AI ? (
                            <div className="widget-section-block">
                                <div className="widget-section-divider widget-ai-kb-divider" />
                                <div className="widget-section-header widget-kb-section-header">
                                    <div>
                                        <div className="widget-section-title">
                                            Knowledge Base <span className="widget-kb-title-count">({knowledgeBaseItemsCount} {knowledgeBaseItemsCount === 1 ? 'item' : 'items'})</span>
                                        </div>
                                        <div className="widget-section-subtitle">Manage the FAQ, policy, and support content the AI bot can use in conversations.</div>
                                    </div>
                                    <Button size="large" onClick={() => setIsKnowledgeBasePanelOpen(true)}>
                                        Manage Knowledge Base
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="widget-section-block">
                                <div className="widget-section-divider widget-ai-kb-divider" />
                                <div className="widget-section-header">
                                    <div className="widget-section-title">Conversation Flow</div>
                                    <div className="widget-section-subtitle">Use quick questions and answers to guide customers before a chat starts.</div>
                                </div>
                                <div className="widget-qna-list">
                                    {[0, 1, 2].map((index) => (
                                        <div className="widget-qna-card" key={`widget-qna-${index}`}>
                                            <div className="widget-subsection-title">Q&amp;A {index + 1}</div>
                                            <div className="widget-qna-input-row">
                                                <span className="widget-qna-prefix">Q</span>
                                                <Input
                                                    className="qna-field"
                                                    maxLength={150}
                                                    value={QA?.[index]?.q}
                                                    onChange={(e) => {
                                                        const nextQA = [...QA];
                                                        nextQA[index] = { q: e.target.value, a: QA?.[index]?.a || '' };
                                                        setQA(nextQA as { q: string; a: string }[]);
                                                    }}
                                                    placeholder="Is WhatsApp support 24/7?"
                                                />
                                            </div>
                                            <div className="widget-qna-input-row">
                                                <span className="widget-qna-prefix">A</span>
                                                <Input
                                                    className="qna-field"
                                                    maxLength={150}
                                                    value={QA?.[index]?.a}
                                                    onChange={(e) => {
                                                        const nextQA = [...QA];
                                                        nextQA[index] = { q: QA?.[index]?.q || '', a: e.target.value };
                                                        setQA(nextQA as { q: string; a: string }[]);
                                                    }}
                                                    placeholder="Almost! We respond between 9am and 6pm."
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    </div>

                    <div className="widget-settings-right">
                    {shop.data?.platformId === 1 ? (
                        <div className="widget-section widget-section-sidebar widget-section-compact">
                            <div className="widget-section-header">
                                <div className="widget-section-title">Shopify Install</div>
                                <div className="widget-section-subtitle">Enable the Vuelve app embed once in your Shopify theme editor. After that, manage the widget here.</div>
                            </div>
                        </div>
                    ) : shop.data?.platformId !== 3 ? (
                        <div
                            className={`widget-settings widget-install-section collapsible ${isEmbedSectionOpen ? 'open' : ''}`}
                            onClick={() => {
                                if (isEmbedSectionOpen) setIsEmbedSectionOpen(false);
                            }}
                        >
                            <button
                                type="button"
                                className="widget-settings-toggle"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsEmbedSectionOpen((value) => !value);
                                }}
                            >
                                <div className="widget-settings-toggle-copy">
                                    <div className="widget-settings-toggle-title">Install Widget</div>
                                    <div className="widget-settings-toggle-subtitle">Embed code and API key</div>
                                </div>
                                <DownOutlined className="widget-settings-toggle-icon" />
                            </button>
                            <div
                                className="widget-settings-content"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {apiKey ? <CopyToClipboardCode
                                    content={`<script src="https://app.vuelve.co/widget/vuelve-widget.min.js" data-api-key="${apiKey}" data-shop-id="${(shop.data.id || 0) * 15}" defer></script>`}/> : null}
                                <Button type="default" size="large"
                                        onClick={updateApiKeyClick}>{apiKey ? 'Re-Generate a new API KEY' : 'Generate a new API KEY'}</Button>
                            </div>
                        </div>
                    ) : null}

                    <div className="widget-section widget-language-section widget-section-compact">
                        <div className="widget-section-header">
                            <div className="widget-section-title">Widget Language</div>
                            <div className="widget-section-subtitle">This sets the widget copy, text direction, and the bot&apos;s default language.</div>
                        </div>
                        <div className="widget-section-block">
                            <Select
                                value={widgetLanguage}
                                onChange={(value) => setWidgetLanguage(value as WidgetLanguage)}
                                style={{ width: '100%' }}
                                showSearch
                                optionFilterProp="children"
                                getPopupContainer={(trigger) => trigger.parentElement!}
                            >
                                {Object.entries(WIDGET_LANGUAGE_CONFIG)
                                    .sort(([, a], [, b]) => a.label.localeCompare(b.label))
                                    .map(([value, config]) => (
                                        <Select.Option key={value} value={value}>
                                            <span className="widget-language-option">
                                                <span className="widget-language-flag">{config.flag}</span>
                                                <span>{config.label}</span>
                                            </span>
                                        </Select.Option>
                                    ))}
                            </Select>
                        </div>
                    </div>

                    <div className="widget-section widget-section-sidebar">
                        <div className="widget-section-header">
                            <div className="widget-section-title">Appearance</div>
                            <div className="widget-section-subtitle">Control the widget shell, trigger, and text color system in one place.</div>
                        </div>
                        <div className="widget-section-block">
                            <div className="widget-appearance-controls">
                                <div className="widget-color-field">
                                    <label>Widget Gradient</label>
                                    <ColorPicker value={gradientPickerValue} mode={['single', 'gradient']} size="large"
                                                 getPopupContainer={(trigger) => trigger.parentElement!}
                                                 onChange={(value, css) => {
                                                     setGradientPickerValue(value.isGradient() ? value.getColors() : value.toCssString());
                                                     setColors(css);
                                                 }}/>
                                </div>
                                <div className="widget-color-field">
                                    <label>Text & Buttons Color 1</label>
                                    <ColorPicker value={textColor1} size="large"
                                                 getPopupContainer={(trigger) => trigger.parentElement!}
                                                 onChange={(value, css) => setTextColor1(css)}/>
                                </div>
                                <div className="widget-color-field">
                                    <label>Text & Buttons Color 2</label>
                                    <ColorPicker value={textColor2} size="large"
                                                 getPopupContainer={(trigger) => trigger.parentElement!}
                                                 onChange={(value, css) => setTextColor2(css)}/>
                                </div>
                            </div>
                            <div className="widget-color-divider" />
                            <div className="widget-color-actions">
                                <Button onClick={() => {
                                    setColors(defaults.colors);
                                    setGradientPickerValue(DEFAULT_COLOR);
                                    setTextColor1(defaults.textColor1);
                                    setTextColor2(defaults.textColor2);
                                }} icon={<UndoOutlined />}>
                                    Return to Default Colors
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className="widget-settings-save">
                        <Button
                            className="main-btn"
                            type="primary"
                            size="large"
                            loading={isLoading}
                            onClick={async () => {
                                await updateSupportWidget(isActive);
                            }}
                            disabled={!isSettingsDirty}
                        >
                            Update Widget's Settings
                        </Button>
                    </div>
                    </div>
                    </div>
                </div>
            : <Loader size={124} /> }
            {/*{shop?.data?.id && apiKey ? <SupportWidgetPreview shopId={shop.data.id} apiKey={apiKey} colorCss={colors} version={previewVersion} /> : null}*/}
        </div>
        <Panel
            isOpen={isKnowledgeBasePanelOpen}
            onClose={closeKnowledgeBasePanel}
            width={768}
            className="widget-knowledge-base-panel"
            darkenScreen={true}
        >
            <div className="widget-knowledge-base-panel-inner">
                <div className="widget-knowledge-base-panel-header">
                    <div className="widget-knowledge-base-panel-header-row">
                        <div className="widget-knowledge-base-panel-title">Knowledge Base</div>
                        <Button
                            size="large"
                            type="primary"
                            onClick={() => setKnowledgeBaseCreateToken((value) => value + 1)}
                        >
                            New Item
                        </Button>
                    </div>
                    <div className="widget-knowledge-base-panel-subtitle">Manage the content your AI bot can rely on.</div>
                </div>
                {isKnowledgeBasePanelOpen ? <KnowledgeBaseEditor panelMode createToken={knowledgeBaseCreateToken} /> : null}
            </div>
        </Panel>
        </Page>
    )
}
